// Platform settings tab — toggle + numeric fields with dirty tracking.

import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Loading } from "@/components/ui/Loading";
import { toast } from "@/stores/toastStore";
import { useStepUp } from "@/context/StepUpContext";
import {
  useAdminSettings,
  useUpdateAdminSettings,
} from "@/hooks/useAdmin";
import type { AdminPlatformSettings } from "@/types/admin";
import { useDict, fmt } from "@/i18n/LocaleProvider";

type SettingsPatch = Partial<AdminPlatformSettings>;

interface ToggleProps {
  label: string;
  description?: string;
  value: boolean;
  changed: boolean;
  onChange: (value: boolean) => void;
}

function Toggle({ label, description, value, changed, onChange }: ToggleProps) {
  const t = useDict().app.adminSettings;
  return (
    <div
      className="px-[14px] py-3 rounded mb-2"
      style={{
        background: "rgb(var(--color-bg))",
        border: `1px solid ${changed ? "rgb(var(--color-warning))" : "rgb(var(--color-stroke))"}`,
      }}
    >
      <div className="flex justify-between items-center">
        <div className="flex-1">
          <div className="font-mono text-[12px] text-content font-semibold">
            {label}
            {changed && (
              <span className="text-warning ml-2 text-[9px]">{t.modified}</span>
            )}
          </div>
          {description && (
            <div className="font-mono text-[10px] text-muted mt-[3px] leading-[1.4]">
              {description}
            </div>
          )}
        </div>
        <button
          onClick={() => onChange(!value)}
          className="w-11 h-6 rounded-xl relative shrink-0 transition-all duration-200 cursor-pointer"
          style={{
            background: value ? "rgb(var(--color-success))" : "rgb(var(--color-bg3))",
            border: `1px solid ${value ? "rgb(var(--color-success))" : "rgb(var(--color-stroke))"}`,
          }}
        >
          <div
            className="absolute top-[2px] w-[18px] h-[18px] rounded-full bg-white transition-[left] duration-200"
            style={{ left: value ? "22px" : "2px" }}
          />
        </button>
      </div>
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  description?: string;
  value: number;
  changed: boolean;
  min: number;
  max: number;
  unit?: string;
  onChange: (value: number) => void;
}

function NumberField({
  label,
  description,
  value,
  changed,
  min,
  max,
  unit,
  onChange,
}: NumberFieldProps) {
  const t = useDict().app.adminSettings;
  return (
    <div
      className="px-[14px] py-3 rounded mb-2"
      style={{
        background: "rgb(var(--color-bg))",
        border: `1px solid ${changed ? "rgb(var(--color-warning))" : "rgb(var(--color-stroke))"}`,
      }}
    >
      <div className="flex justify-between items-center gap-3">
        <div className="flex-1">
          <div className="font-mono text-[12px] text-content font-semibold">
            {label}
            {changed && (
              <span className="text-warning ml-2 text-[9px]">{t.modified}</span>
            )}
          </div>
          {description && (
            <div className="font-mono text-[10px] text-muted mt-[3px] leading-[1.4]">
              {description}
            </div>
          )}
        </div>
        <div className="flex items-center gap-[6px]">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value) || 0)}
            min={min}
            max={max}
            className="w-20 px-[10px] py-[7px] bg-bg2 border border-stroke rounded text-content font-mono text-[12px] outline-none text-right"
          />
          {unit && (
            <span className="font-mono text-[10px] text-muted w-10">{unit}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminSettingsTab() {
  const t = useDict().app.adminSettings;
  const { withStepUp } = useStepUp();
  const settings = useAdminSettings();
  const update = useUpdateAdminSettings();
  const [dirty, setDirty] = useState<SettingsPatch>({});

  if (settings.isLoading || !settings.data) {
    return <Loading text={t.loading} />;
  }

  const current = settings.data;
  const get = <K extends keyof AdminPlatformSettings>(
    key: K,
  ): AdminPlatformSettings[K] =>
    key in dirty ? (dirty[key] as AdminPlatformSettings[K]) : current[key];

  function setField<K extends keyof AdminPlatformSettings>(
    key: K,
    value: AdminPlatformSettings[K],
  ) {
    setDirty((d) => ({ ...d, [key]: value }));
  }

  function isChanged(key: keyof AdminPlatformSettings): boolean {
    return key in dirty;
  }

  const hasDirty = Object.keys(dirty).length > 0;

  function save() {
    if (!hasDirty) {
      toast.error(t.noChanges);
      return;
    }
    withStepUp(async (token) => {
      try {
        await update.mutateAsync({ patch: dirty, stepUpToken: token });
        setDirty({});
        toast.success(t.settingsSaved);
      } catch (e) {
        toast.error(
          `${t.saveFailed} ${e instanceof Error ? e.message : String(e)}`,
        );
      }
    });
  }

  function revert() {
    setDirty({});
    toast.success(t.changesReverted);
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-[14px]">
        <div>
          <div className="font-mono text-[10px] text-muted">
            {t.lastUpdated}{" "}
            {current.updated_at
              ? new Date(current.updated_at).toLocaleString()
              : t.never}
            {current.updated_by_user_id &&
              fmt(t.byUser, { id: current.updated_by_user_id })}
          </div>
        </div>
        <div className="flex gap-2">
          {hasDirty && (
            <Button variant="default" onClick={revert}>
              {t.revert}
            </Button>
          )}
          <Button variant="primary" onClick={save} disabled={!hasDirty}>
            {hasDirty
              ? fmt(t.save, { n: Object.keys(dirty).length })
              : t.noChanges}
          </Button>
        </div>
      </div>

      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 mt-1">
        {t.secRegistration}
      </div>
      <Toggle
        label={t.regEnabled}
        description={t.regEnabledDesc}
        value={get("registration_enabled")}
        changed={isChanged("registration_enabled")}
        onChange={(v) => setField("registration_enabled", v)}
      />

      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 mt-4">
        {t.secOauth}
      </div>
      <Toggle
        label={t.googleEnabled}
        description={t.googleDesc}
        value={get("oauth_google_enabled")}
        changed={isChanged("oauth_google_enabled")}
        onChange={(v) => setField("oauth_google_enabled", v)}
      />
      <Toggle
        label={t.githubEnabled}
        description={t.githubDesc}
        value={get("oauth_github_enabled")}
        changed={isChanged("oauth_github_enabled")}
        onChange={(v) => setField("oauth_github_enabled", v)}
      />

      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 mt-4">
        {t.secQuotas}
      </div>
      <NumberField
        label={t.maxEng}
        description={t.maxEngDesc}
        value={get("max_engagements_per_org")}
        changed={isChanged("max_engagements_per_org")}
        min={0}
        max={100000}
        unit={t.unitEng}
        onChange={(v) => setField("max_engagements_per_org", v)}
      />
      <NumberField
        label={t.maxScans}
        description={t.maxScansDesc}
        value={get("max_scans_per_day_per_user")}
        changed={isChanged("max_scans_per_day_per_user")}
        min={0}
        max={10000}
        unit={t.unitScans}
        onChange={(v) => setField("max_scans_per_day_per_user", v)}
      />

      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 mt-4">
        {t.secAudit}
      </div>
      <NumberField
        label={t.auditRetention}
        description={t.auditRetentionDesc}
        value={get("audit_retention_days")}
        changed={isChanged("audit_retention_days")}
        min={30}
        max={3650}
        unit={t.unitDays}
        onChange={(v) => setField("audit_retention_days", v)}
      />

      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 mt-4">
        {t.secAI}
      </div>
      <Toggle
        label={t.aiEnabled}
        description={t.aiDesc}
        value={get("ai_enrichment_enabled")}
        changed={isChanged("ai_enrichment_enabled")}
        onChange={(v) => setField("ai_enrichment_enabled", v)}
      />

      <div
        className="px-[14px] py-3 rounded mt-4 font-mono text-[10px] text-muted leading-[1.6]"
        style={{
          background: "rgb(var(--color-bg2))",
          border: "1px dashed rgb(var(--color-stroke))",
        }}
      >
        💡 <b>{t.noteTitle}</b> {t.noteBody}
      </div>
    </div>
  );
}
